import {
  Badge,
  Box,
  Divider,
  Group,
  Menu,
  Modal,
  Portal,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconDotsVertical, IconDownload, IconEdit, IconPlus, IconStar, IconTrashX } from '@tabler/icons-react';
import useFileDownload from 'api/use-file-download';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import FileLink from 'components/files/FileLink';
import FileThumbnail from 'components/files/FileThumbnail';
import UploadFiles from 'components/files/upload/UploadFiles';
import { IFileState } from 'components/files/upload/types';
import { useConfirm } from 'components/modals/message/MessageProvider';
import Slider from 'components/modals/slider/Slider';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import P3Regular from 'components/typography/P3Regular';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

/** One persisted or staged HR file row rendered by the combined person form. */
export interface HrPersonFormFile {
  key: string;
  personFileId?: number;
  version?: number;
  fileId: string;
  fileName: string;
  displayName: string;
  categoryId: string;
  categoryName?: string;
  uploadedAt?: string;
  mimeType?: string;
  isPrimaryPhoto?: boolean;
  wasPrimaryPhoto?: boolean;
  changed: boolean;
  deleted: boolean;
}

interface HrFileCategoryOption {
  categoryId: number;
  name?: string;
}

interface HrPersonFilesProps {
  files: HrPersonFormFile[];
  categories: HrFileCategoryOption[];
  readonly?: boolean;
  onAdd: (files: IFileState[], category: { categoryId: string; categoryName: string }) => void;
  onChange: (index: number, values: Partial<HrPersonFormFile>) => void;
  onRemove: (index: number) => void;
  onSetPrimary: (index: number) => void;
}

/** DeviceFiles-compatible presentation for editable or read-only HR person files. */
export default function HrPersonFiles({
  files,
  categories,
  readonly = false,
  onAdd,
  onChange,
  onRemove,
  onSetPrimary,
}: HrPersonFilesProps) {
  const theme = useMantineTheme();
  const { isMobile } = useResponsiveDesign();
  const { download } = useFileDownload();
  const { confirm } = useConfirm();
  const [activeTab, setActiveTab] = useState('');
  const [addOpened, setAddOpened] = useState(false);
  const [filesToAdd, setFilesToAdd] = useState<IFileState[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [openedSlider, setOpenedSlider] = useState(false);
  const [initialFileId, setInitialFileId] = useState('');
  const [categoryFileIndex, setCategoryFileIndex] = useState<number | null>(null);

  const visibleCategories = useMemo(() => {
    const options = new Map<string, string>();
    categories.forEach((category) =>
      options.set(String(category.categoryId), category.name ?? `Kategória ${category.categoryId}`)
    );
    files.forEach((file) => {
      if (file.categoryId && !options.has(file.categoryId)) {
        options.set(file.categoryId, file.categoryName ?? `Kategória ${file.categoryId}`);
      }
    });

    return [...options].map(([value, label]) => ({ value, label }));
  }, [categories, files]);

  useEffect(() => {
    if (!visibleCategories.some(({ value }) => value === activeTab)) {
      setActiveTab(visibleCategories[0]?.value ?? '');
    }
  }, [activeTab, visibleCategories]);

  const addLoading = filesToAdd.some(({ finalized }) => !finalized);
  const addDisabled = filesToAdd.length === 0 || !selectedCategoryId;
  const gridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: '24px 80px 4fr 3fr 4fr 40px',
      gap: '24px',
      borderRadius: '4px',
      alignItems: 'center',
    }),
    []
  );

  /** Opens the add-files modal with the current category preselected. */
  const openAdd = () => {
    setFilesToAdd([]);
    setSelectedCategoryId(activeTab || visibleCategories[0]?.value || '');
    setAddOpened(true);
  };

  /** Adds finalized File Manager uploads to the parent person form. */
  const addFiles = () => {
    const category = visibleCategories.find(({ value }) => value === selectedCategoryId);
    if (!category || addDisabled || addLoading) {
      return;
    }

    onAdd(
      filesToAdd.filter(({ fileId }) => Boolean(fileId)),
      {
        categoryId: category.value,
        categoryName: category.label,
      }
    );
    setActiveTab(category.value);
    setAddOpened(false);
  };

  /** Opens the single-file document preview used by DeviceFiles for non-photo files. */
  const openSlider = (fileId: string) => {
    setInitialFileId(fileId);
    setOpenedSlider(true);
  };

  /** Confirms staged removal or permanent deletion of the HR association. */
  const confirmRemove = (file: HrPersonFormFile & { globalIndex: number }) => {
    const persisted = Boolean(file.personFileId);
    confirm({
      title: persisted ? 'Vymazať súbor' : 'Odobrať súbor',
      content: persisted
        ? `Naozaj si prajete natrvalo vymazať súbor „${
            file.displayName || file.fileName
          }“ z HR evidencie? Túto operáciu nie je možné vrátiť späť.`
        : `Naozaj si prajete odobrať súbor „${file.displayName || file.fileName}“ z formulára?`,
      primaryButtonText: persisted ? 'Vymazať' : 'Odobrať',
      secondaryButtonText: 'Zrušiť',
      onConfirm: () => onRemove(file.globalIndex),
    });
  };

  if (visibleCategories.length === 0) {
    return (
      <Box py={24} px={16}>
        <P1Medium>
          {readonly ? 'Osoba nemá prístupné súbory.' : 'Pre vybranú organizáciu nie sú dostupné kategórie súborov.'}
        </P1Medium>
      </Box>
    );
  }

  return (
    <>
      <Tabs
        value={activeTab}
        onTabChange={(value) => setActiveTab(value ?? '')}
        styles={(theme) => ({
          tab: {
            '&[data-active]': {
              boxShadow: `inset 0 -2px 0 ${theme.colors.red[8]}`,
            },
          },
        })}
      >
        <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Tabs.List sx={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
            {visibleCategories.map((category) => {
              const count = files.filter((file) => file.categoryId === category.value && !file.deleted).length;
              return (
                <Tabs.Tab key={category.value} value={category.value}>
                  <Group spacing={6} align="center">
                    <span>{category.label}</span>
                    <Badge
                      color={activeTab === category.value ? 'red' : 'gray.5'}
                      size="sm"
                      variant="filled"
                      mt={2}
                      px={5}
                    >
                      <Box pt={1}>{count}</Box>
                    </Badge>
                  </Group>
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Box>

        {visibleCategories.map((category) => {
          const categoryFiles = files
            .map((file, index) => ({ ...file, globalIndex: index }))
            .filter((file) => file.categoryId === category.value);
          return (
            <Tabs.Panel key={category.value} value={category.value}>
              <Box pt={24}>
                {!isMobile && (
                  <Box sx={gridStyle} bg="gray.0" p={16}>
                    <span />
                    <P1Medium color="gray.8" pl={4}>
                      Náhľad
                    </P1Medium>
                    <P1Medium color="gray.8">Názov</P1Medium>
                    <P1Medium color="gray.8">Súbor</P1Medium>
                    <P1Medium color="gray.8">Nahratý</P1Medium>
                  </Box>
                )}
                {(categoryFiles.length === 0 || categoryFiles.every(({ deleted }) => deleted)) && (
                  <Box py={24} px={16}>
                    <P1Medium> Momentálne nie sú nahraté žiadne súbory</P1Medium>
                  </Box>
                )}
                {categoryFiles.map((file) => {
                  if (file.deleted) {
                    return null;
                  }

                  const uploadedAt = file.uploadedAt ? new Date(file.uploadedAt) : null;
                  const uploadedLabel =
                    uploadedAt && !Number.isNaN(uploadedAt.getTime()) ? uploadedAt.toLocaleString() : '';
                  const eligiblePrimaryPhoto = file.mimeType?.startsWith('image/') ?? false;
                  const primaryPhotoStar = !eligiblePrimaryPhoto ? null : file.isPrimaryPhoto ? (
                    <Group
                      w={isMobile ? undefined : 24}
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : onSetPrimary(file.globalIndex))}
                    >
                      <Tooltip
                        label={readonly ? 'Hlavný obrázok' : 'Zrušiť hlavný obrázok'}
                        withArrow={false}
                        openDelay={500}
                      >
                        <IconStar stroke={2} color="#E6B24E" fill="#E6B24E" />
                      </Tooltip>
                      {isMobile && <P2Regular>{readonly ? 'Hlavný obrázok' : 'Zrušiť hlavný obrázok'}</P2Regular>}
                    </Group>
                  ) : (
                    <Group
                      w={isMobile ? undefined : 24}
                      style={{ cursor: readonly ? 'not-allowed' : 'pointer' }}
                      onClick={() => (readonly ? noop : onSetPrimary(file.globalIndex))}
                    >
                      <Tooltip label="Nastaviť ako hlavný obrázok" withArrow={false} openDelay={500}>
                        <IconStar stroke={2} color={`${theme.colors.gray[5]}`} />
                      </Tooltip>
                      {isMobile && <P2Regular>Nastaviť ako hlavný obrázok</P2Regular>}
                    </Group>
                  );
                  return (
                    <Box key={file.key}>
                      {isMobile ? (
                        <Stack spacing={16} p={16}>
                          {primaryPhotoStar}
                          <Stack spacing={8}>
                            <P3Regular color="gray.6">Náhľad</P3Regular>
                            <Group w={80}>
                              <FileThumbnail fileId={file.fileId} onOpenSlider={() => openSlider(file.fileId)} />
                            </Group>
                          </Stack>
                          <Stack spacing={8}>
                            <P3Regular color="gray.6">Názov</P3Regular>
                            <TextInput
                              size="sm"
                              readOnly={readonly}
                              value={file.displayName}
                              onChange={
                                readonly
                                  ? undefined
                                  : (event) => onChange(file.globalIndex, { displayName: event.currentTarget.value })
                              }
                              name={`objekto-files.hr.${file.globalIndex}.displayName`}
                            />
                          </Stack>
                          <Stack spacing={8}>
                            <P3Regular color="gray.6">Súbor</P3Regular>
                            <FileLink fileId={file.fileId} onOpenSlider={() => openSlider(file.fileId)} />
                          </Stack>
                          <Stack spacing={8}>
                            <P3Regular color="gray.6">Nahratý</P3Regular>
                            <P2Regular>{uploadedLabel}</P2Regular>
                          </Stack>
                          <Group position="right">
                            <Menu position="bottom-end">
                              <Menu.Target>
                                <ActionIcon
                                  variant="tertiary"
                                  size="sm"
                                  aria-label={`Akcie súboru ${file.displayName}`}
                                >
                                  <IconDotsVertical stroke="1.5" />
                                </ActionIcon>
                              </Menu.Target>
                              <Portal>
                                <Menu.Dropdown>
                                  <Menu.Label>
                                    <Text maw={160} truncate title={file.displayName || file.fileName}>
                                      {file.displayName || file.fileName}
                                    </Text>
                                  </Menu.Label>
                                  <Menu.Item
                                    onClick={() => download(file.fileId)}
                                    icon={<IconDownload stroke={1.5} size={20} />}
                                  >
                                    Stiahnuť
                                  </Menu.Item>
                                  {!readonly && (
                                    <>
                                      <Menu.Item
                                        onClick={() => setCategoryFileIndex(file.globalIndex)}
                                        icon={<IconEdit stroke={1.5} size={20} />}
                                      >
                                        Zmeniť kategóriu
                                      </Menu.Item>
                                      <Menu.Item
                                        color="red.8"
                                        onClick={() => confirmRemove(file)}
                                        icon={<IconTrashX stroke={1.5} size={20} />}
                                      >
                                        {file.personFileId ? 'Vymazať' : 'Odobrať'}
                                      </Menu.Item>
                                    </>
                                  )}
                                </Menu.Dropdown>
                              </Portal>
                            </Menu>
                          </Group>
                        </Stack>
                      ) : (
                        <Box sx={gridStyle} p={16}>
                          {primaryPhotoStar ?? <Group w={24} />}
                          <Group w={80}>
                            <FileThumbnail fileId={file.fileId} onOpenSlider={() => openSlider(file.fileId)} />
                          </Group>
                          <TextInput
                            size="sm"
                            readOnly={readonly}
                            value={file.displayName}
                            onChange={
                              readonly
                                ? undefined
                                : (event) => onChange(file.globalIndex, { displayName: event.currentTarget.value })
                            }
                            name={`objekto-files.hr.${file.globalIndex}.displayName`}
                          />
                          <FileLink fileId={file.fileId} onOpenSlider={() => openSlider(file.fileId)} />
                          <P2Regular>{uploadedLabel}</P2Regular>
                          <Menu position="bottom-end">
                            <Menu.Target>
                              <ActionIcon
                                variant="tertiary"
                                size={isMobile ? 'sm' : 'md'}
                                aria-label={`Akcie súboru ${file.displayName}`}
                              >
                                <IconDotsVertical stroke="1.5" />
                              </ActionIcon>
                            </Menu.Target>
                            <Portal>
                              <Menu.Dropdown>
                                <Menu.Label>
                                  <Text maw={160} truncate title={file.displayName || file.fileName}>
                                    {file.displayName || file.fileName}
                                  </Text>
                                </Menu.Label>
                                <Menu.Item
                                  onClick={() => download(file.fileId)}
                                  icon={<IconDownload stroke={1.5} size={20} />}
                                >
                                  Stiahnuť
                                </Menu.Item>
                                {!readonly && (
                                  <>
                                    <Menu.Item
                                      onClick={() => setCategoryFileIndex(file.globalIndex)}
                                      icon={<IconEdit stroke={1.5} size={20} />}
                                    >
                                      Zmeniť kategóriu
                                    </Menu.Item>
                                    <Menu.Item
                                      color="red.8"
                                      onClick={() => confirmRemove(file)}
                                      icon={<IconTrashX stroke={1.5} size={20} />}
                                    >
                                      {file.personFileId ? 'Vymazať' : 'Odobrať'}
                                    </Menu.Item>
                                  </>
                                )}
                              </Menu.Dropdown>
                            </Portal>
                          </Menu>
                        </Box>
                      )}
                      <Divider color="gray.1" />
                    </Box>
                  );
                })}
              </Box>
            </Tabs.Panel>
          );
        })}
      </Tabs>

      {!readonly && (
        <Button size="md" mt={24} variant="subtle" leftIcon={<IconPlus stroke="1.5" />} onClick={openAdd}>
          Pridať súbory
        </Button>
      )}

      <Slider
        key={initialFileId}
        opened={openedSlider}
        fileIds={[initialFileId]}
        onClose={() => setOpenedSlider(false)}
        initialImageId={initialFileId}
        hideControls
      />

      {!readonly && (
        <>
          <Modal
            opened={addOpened}
            onClose={() => setAddOpened(false)}
            title="Pridať súbory"
            size="auto"
            closeOnClickOutside={!addLoading}
            closeOnEscape={!addLoading}
            closeButtonProps={{ loading: addLoading }}
          >
            <Stack spacing={24}>
              <Select
                label="Kategória súboru"
                size="md"
                data={visibleCategories}
                value={selectedCategoryId}
                onChange={(value) => setSelectedCategoryId(value ?? '')}
                withinPortal
              />
              <Box pt={12}>
                <UploadFiles inputSize="md" onChange={(value) => setFilesToAdd(value ?? [])} />
              </Box>
              <Group position="right">
                <Button variant="subtle-gray" onClick={() => setAddOpened(false)} disabled={addLoading}>
                  Zrušiť
                </Button>
                <Button variant="primary" type="button" onClick={addFiles} loading={addLoading} disabled={addDisabled}>
                  Pridať súbory
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={categoryFileIndex !== null}
            onClose={() => setCategoryFileIndex(null)}
            title="Zmeniť kategóriu súboru"
          >
            <Stack spacing={24}>
              <Select
                label="Kategória súboru"
                size="md"
                data={visibleCategories}
                value={categoryFileIndex === null ? null : files[categoryFileIndex]?.categoryId}
                onChange={(value) => {
                  if (categoryFileIndex === null || !value) {
                    return;
                  }
                  const category = visibleCategories.find((candidate) => candidate.value === value);
                  if (!category) {
                    return;
                  }
                  onChange(categoryFileIndex, { categoryId: category.value, categoryName: category.label });
                  setActiveTab(category.value);
                  setCategoryFileIndex(null);
                }}
                withinPortal
              />
              <Group position="right">
                <Button type="button" variant="subtle-gray" onClick={() => setCategoryFileIndex(null)}>
                  Zrušiť
                </Button>
              </Group>
            </Stack>
          </Modal>
        </>
      )}
    </>
  );
}
